import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getBackendUrl } from '../../lib/env';
import { getShopUrl } from '../../lib/domains';

const API_URL = getBackendUrl();

function money(currency, value) {
  return `${currency} ${Number(value || 0).toLocaleString()}`;
}

function ensureMeta(name, content, attr = 'name') {
  if (!content) return;
  const selector = attr === 'property' ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

export default function StorePublic(props = {}) {
  const params = useParams();
  const { resolvedUsername, resolvedProductSlug, publicPathPrefix: resolvedPathPrefix } = props;
  const username = resolvedUsername || params.username || '';
  const productSlug = resolvedProductSlug || params.productSlug || null;
  const pathPrefix = resolvedPathPrefix !== undefined ? resolvedPathPrefix : (username ? `/${username}` : '');
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const endpoint = productSlug
      ? `${API_URL}/api/public/stores/${username}/product/${productSlug}`
      : `${API_URL}/api/public/stores/${username}`;
    const params = {};
    if (!productSlug) {
      if (query) params.q = query;
      if (category) params.category = category;
    }
    axios.get(endpoint, { params }).then(r => setData(r.data)).catch(() => setData(null));
  }, [username, productSlug, query, category]);

  useEffect(() => {
    if (!data) return;
    const store = data.store;
    const product = data.product;
    const title = product ? `${product.seo_title || product.name} | ${store.store_name}` : `${store.seo_title || store.store_name} | Jjuma Store`;
    const description = product ? (product.seo_description || product.short_description || store.short_description || '') : (store.seo_description || store.short_description || '');
    const image = product
      ? (product.images?.[0] || store.social_sharing_image_url || store.cover_image_url || store.logo_url || '')
      : (store.social_sharing_image_url || store.cover_image_url || store.logo_url || '');
    const canonical = product
      ? getShopUrl(`/${store.username}/product/${product.slug}`)
      : getShopUrl(`/${store.username}`);
    document.title = title;
    ensureMeta('description', description);
    ensureMeta('keywords', product ? (product.seo_keywords || []).join(', ') : (store.seo_keywords || []).join(', '));
    ensureMeta('og:title', title, 'property');
    ensureMeta('og:description', description, 'property');
    ensureMeta('og:image', image, 'property');
    ensureMeta('og:url', canonical, 'property');
    ensureMeta('og:site_name', 'Jjuma Pay', 'property');
    ensureMeta('og:type', product ? 'product' : 'website', 'property');
    ensureMeta('og:image:alt', product ? product.name : store.store_name, 'property');
    ensureMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    ensureMeta('twitter:title', title);
    ensureMeta('twitter:description', description);
    ensureMeta('twitter:image', image);
    ensureMeta('twitter:url', canonical);
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);
  }, [data]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-xl bg-white border border-slate-200 rounded-[28px] shadow-sm">
          <CardContent className="p-8 text-center">
            <p className="text-slate-500">Store not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const store = data.store;
  const products = data.products || [];
  const product = data.product || null;
  const relatedProducts = data.related_products || [];
  const categories = data.categories || [];
  const buildPath = (suffix = '') => {
    const base = pathPrefix || '';
    if (!suffix) return base || '/';
    const cleanSuffix = suffix.startsWith('/') ? suffix : `/${suffix}`;
    return `${base}${cleanSuffix}` || '/';
  };
  const buildStoreHomePath = (categoryName = '') => {
    const base = pathPrefix || '/';
    const safeBase = base === '/' ? '' : base;
    return `${safeBase}${categoryName ? `?category=${encodeURIComponent(categoryName)}` : ''}` || '/';
  };
  const filtered = products.filter(p => {
    const matchesSearch = `${p.name} ${p.category} ${p.short_description}`.toLowerCase().includes(query.toLowerCase());
    const matchesCategory = !category || (p.category || '').toLowerCase() === category.toLowerCase();
    return matchesSearch && matchesCategory;
  });
  const rawWhatsapp = String(store.whatsapp_number || '').replace(/[^\d+]/g, '');
  const shopWhatsapp = rawWhatsapp.startsWith('+') ? rawWhatsapp : rawWhatsapp ? `+${rawWhatsapp}` : '';
  const currentUrl = product
    ? getShopUrl(`/${store.username}/product/${product.slug}`)
    : getShopUrl(`/${store.username}`);
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  const shareStore = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product ? `${product.seo_title || product.name} | ${store.store_name}` : `${store.seo_title || store.store_name} | Jjuma Store`,
          text: product ? (product.seo_description || product.short_description || store.short_description || '') : (store.seo_description || store.short_description || ''),
          url: currentUrl,
        });
        return;
      } catch {}
    }
    copyShareLink();
  };

  const openWhatsApp = (message) => {
    if (!shopWhatsapp) return;
    const phone = shopWhatsapp.replace(/[^\d]/g, '');
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderProductCard = (item) => (
    <Card key={item.id} className="h-full bg-white border border-slate-200 shadow-sm rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
      <div className="h-44 bg-slate-100">
        {item.images?.[0] ? <img src={item.images[0]} alt={item.name} className="h-full w-full object-cover" /> : null}
      </div>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h2 className="font-medium text-slate-900 line-clamp-2">{item.name}</h2>
          <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">{money(item.currency, item.sale_price || item.price)}</span>
        </div>
        <p className="text-xs text-slate-500 line-clamp-2">{item.short_description}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)]">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <Card className="mb-4 border border-slate-200 bg-white/90 shadow-sm rounded-[22px]">
          <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">
                {product ? 'Share product' : 'Share store'}
              </p>
              <p className="text-sm text-slate-600 break-all">{currentUrl}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" className="border-slate-200 bg-white" onClick={copyShareLink}>
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={shareStore}>
                Share
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] rounded-[28px]">
          <div className="h-48 bg-slate-100 relative">
            {store.cover_image_url ? (
              <img src={store.cover_image_url} alt={store.store_name} className="h-full w-full object-cover" />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
            <div className="absolute left-4 bottom-4 flex items-end gap-4">
              <div className="h-20 w-20 rounded-2xl overflow-hidden border-4 border-white bg-white shadow-md">
                {store.logo_url ? <img src={store.logo_url} alt={store.store_name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-2xl">{store.store_name?.charAt(0)?.toUpperCase() || 'S'}</div>}
              </div>
              <div className="text-white">
                <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{store.store_name}</h1>
                <p className="text-sm text-white/90">{store.short_description}</p>
              </div>
            </div>
          </div>

          <CardContent className="p-4 sm:p-6 space-y-5">
            {product ? (
              <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-4">
                  <div className="rounded-[24px] overflow-hidden border border-slate-100 bg-slate-50">
                    <div className="aspect-[4/3] bg-slate-100">
                      {product.images?.[0] ? <img src={product.images[0]} alt={product.name} className="h-full w-full object-cover" /> : null}
                    </div>
                  </div>
                  {product.images?.length > 1 ? (
                    <div className="grid grid-cols-4 gap-2">
                      {product.images.slice(0, 4).map((img, index) => (
                        <div key={`${product.id}-img-${index}`} className="aspect-square overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
                          <img src={img} alt={`${product.name} ${index + 1}`} className="h-full w-full object-cover" />
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                      <button
                        type="button"
                        className="font-medium text-emerald-700 hover:text-emerald-800"
                        onClick={() => window.location.href = buildStoreHomePath('')}
                      >
                        Back to store
                      </button>
                      <span>/</span>
                      <span>Product</span>
                    </div>
                    <p className="text-xs uppercase tracking-[0.24em] text-emerald-700 font-semibold">Product details</p>
                    <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{product.name}</h2>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <span className="text-2xl font-semibold text-emerald-700">{money(product.currency, product.sale_price || product.price)}</span>
                      {product.sale_price ? <span className="text-sm text-slate-400 line-through">{money(product.currency, product.price)}</span> : null}
                    </div>
                  </div>

                  <p className="text-sm text-slate-600 leading-7">{product.full_description || product.short_description}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button
                        className="bg-slate-900 hover:bg-slate-800 text-white h-12"
                        onClick={() => window.location.href = buildPath(`/checkout?product=${product.slug}`)}
                      >
                        Checkout
                      </Button>
                    <Button
                      className="bg-emerald-700 hover:bg-emerald-800 text-white h-12"
                      onClick={() => openWhatsApp(`Hi ${store.store_name}, I would like to buy ${product.name} for ${money(product.currency, product.sale_price || product.price)}.`)}
                      disabled={!shopWhatsapp}
                    >
                      Buy now
                    </Button>
                    <Button
                      variant="outline"
                      className="border-emerald-200 text-emerald-800 hover:bg-emerald-50 h-12"
                      onClick={() => openWhatsApp(`Hi ${store.store_name}, I would like to order ${product.name}.`)}
                      disabled={!shopWhatsapp}
                    >
                      Order via WhatsApp
                    </Button>
                  </div>

                  {!shopWhatsapp ? (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-2xl p-3">
                      WhatsApp number is not set for this store yet.
                    </p>
                  ) : null}

                  {categories.length > 0 ? (
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">Browse by category</h3>
                          <p className="text-xs text-slate-500">Jump back to the shop with this category selected.</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-slate-200 text-slate-700"
                          onClick={() => window.location.href = buildStoreHomePath('')}
                        >
                          All products
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {categories.slice(0, 8).map((item) => (
                          <Button
                            key={item.id}
                            type="button"
                            variant="outline"
                            size="sm"
                            className={product.category === item.name ? 'border-emerald-200 text-emerald-800 bg-emerald-50' : 'border-slate-200 text-slate-700'}
                            onClick={() => window.location.href = buildStoreHomePath(item.name)}
                          >
                            {item.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search products..."
                className="bp-input h-12"
                disabled={!!product}
              />
              <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-xs text-slate-500">WhatsApp</p>
                  <p className="text-sm font-medium text-slate-900">{store.whatsapp_number || 'Not set'}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => openWhatsApp(`Hi ${store.store_name}, I need help with your store.`)} disabled={!shopWhatsapp}>Contact</Button>
              </div>
            </div>

            {!product && categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant={category ? 'outline' : 'default'} className={category ? 'border-slate-200 text-slate-700' : 'bg-emerald-700 hover:bg-emerald-800 text-white'} onClick={() => setCategory('')}>
                  All categories
                </Button>
                {categories.map((item) => (
                  <Button
                    key={item.id}
                    type="button"
                    variant="outline"
                    className={category === item.name ? 'border-emerald-200 text-emerald-800 bg-emerald-50' : 'border-slate-200 text-slate-700'}
                    onClick={() => setCategory(category === item.name ? '' : item.name)}
                  >
                    {item.name}
                  </Button>
                ))}
              </div>
            )}

            {product ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Related products</h3>
                  <p className="text-sm text-slate-500">More items from this store.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedProducts.map((item) => (
                    <a key={item.id} href={buildPath(`/product/${item.slug}`)} className="block">
                      {renderProductCard(item)}
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((item) => (
                  <a key={item.id} href={buildPath(`/product/${item.slug}`)} className="block">
                    {renderProductCard(item)}
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 py-6">
          {store.remove_branding ? null : 'Powered by Jjuma Pay'}
        </p>
      </div>
    </div>
  );
}
